import React, { useState, useEffect, useMemo } from "react";
import api from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import { ComparisonFilters } from "./ComparisonFilters";
import { RadarComparison } from "./RadarComparison";
import { HeatmapComparison } from "./HeatmapComparison";
import { GroupedBarComparison } from "./GroupedBarComparison";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { useGlobalComparisonPdfExport } from "@/hooks/useGlobalComparisonPdfExport";
import { useToast } from "@/hooks/use-toast";

interface CandidatDetail {
  id: string;
  nom: string;
  score: number;
  decision: string;
  created_at: string;
  model_used?: string;
  scores_criteres?: { [key: string]: number };
}

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  decision_counts: {
    shortlist: number;
    consider: number;
    reject: number;
  };
  candidats: CandidatDetail[];
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface GlobalSocieteData {
  societe_id: string;
  societe_nom: string;
  note_qualite_offre: number | null;
  note_prix: number | null;
  note_durabilite: number | null;
  note_references: number | null;
  poids_qualite_offre: number;
  poids_prix: number;
  poids_durabilite: number;
  poids_references: number;
  score_pondere: number;
}

interface GlobalComparisonViewProps {
  aoId: string;
  appelOffre?: { id: string; titre: string; reference: string };
  selectedCandidatesData?: SocieteRanking[];
}

export const GlobalComparisonView: React.FC<GlobalComparisonViewProps> = ({ aoId, appelOffre, selectedCandidatesData }) => {
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>(['overall_score']);
  const [visualizationType, setVisualizationType] = useState<string>('heatmap');
  const [filterMode, setFilterMode] = useState<'top' | 'manual' | 'decision'>('top');
  const [topCount, setTopCount] = useState<number>(5);
  const [selectedDecisions, setSelectedDecisions] = useState<string[]>(['shortlist', 'consider']);
  
  const { exportGlobalComparisonToPdf } = useGlobalComparisonPdfExport();
  const { toast } = useToast();

  // Fetch societes with notes for this AO
  const { data: societeNotes, isLoading } = useQuery({
    queryKey: ['ao-societe-notes-comparison', aoId],
    queryFn: async () => {
      // Fetch notes via API
      const { data: notesResponse } = await api.get(`/appels-offres/${aoId}/societe-notes`);
      const notesData = notesResponse?.data || [];

      // Fetch societes that have depots for this AO via API
      const { data: societesResponse } = await api.get(`/appels-offres/${aoId}/societes`);
      const societesData = societesResponse?.data || [];

      // Get unique societes
      const societesMap = new Map<string, { id: string; nom: string }>();
      societesData.forEach((societe: any) => {
        if (!societesMap.has(societe.id)) {
          societesMap.set(societe.id, { id: societe.id, nom: societe.nom });
        }
      });

      // Merge notes with societes
      const notesMap = new Map(notesData.map((n: any) => [n.societeId, {
        ...n,
        societe_id: n.societeId,
        note_qualite_offre: n.noteQualiteOffre,
        note_prix: n.notePrix,
        note_durabilite: n.noteDurabilite,
        note_references: n.noteReferences,
        poids_qualite_offre: n.poidsQualiteOffre,
        poids_prix: n.poidsPrix,
        poids_durabilite: n.poidsDurabilite,
        poids_references: n.poidsReferences
      }]));

      return { societesMap, notesMap };
    },
    enabled: !!aoId
  });

  // Transform data for PDF export
  const globalSocietesData: GlobalSocieteData[] = useMemo(() => {
    if (!societeNotes) return [];

    const { societesMap, notesMap } = societeNotes;
    const result: GlobalSocieteData[] = [];

    societesMap.forEach(societe => {
      const note = notesMap.get(societe.id);
      
      const poids_qualite_offre = note?.poids_qualite_offre ?? 25;
      const poids_prix = note?.poids_prix ?? 25;
      const poids_durabilite = note?.poids_durabilite ?? 25;
      const poids_references = note?.poids_references ?? 25;
      
      const note_qualite_offre = note?.note_qualite_offre ?? null;
      const note_prix = note?.note_prix ?? null;
      const note_durabilite = note?.note_durabilite ?? null;
      const note_references = note?.note_references ?? null;
      
      let totalWeight = 0;
      let weightedSum = 0;
      
      if (note_qualite_offre !== null) {
        weightedSum += note_qualite_offre * poids_qualite_offre;
        totalWeight += poids_qualite_offre;
      }
      if (note_prix !== null) {
        weightedSum += note_prix * poids_prix;
        totalWeight += poids_prix;
      }
      if (note_durabilite !== null) {
        weightedSum += note_durabilite * poids_durabilite;
        totalWeight += poids_durabilite;
      }
      if (note_references !== null) {
        weightedSum += note_references * poids_references;
        totalWeight += poids_references;
      }
      
      const score_pondere = totalWeight > 0 ? weightedSum / totalWeight : 0;
      
      result.push({
        societe_id: societe.id,
        societe_nom: societe.nom,
        note_qualite_offre,
        note_prix,
        note_durabilite,
        note_references,
        poids_qualite_offre,
        poids_prix,
        poids_durabilite,
        poids_references,
        score_pondere
      });
    });

    return result;
  }, [societeNotes]);

  // Transform data to match SocieteRanking format for ComparisonFilters and visualizations
  const rankings: SocieteRanking[] = useMemo(() => {
    if (!societeNotes) return [];

    const { societesMap, notesMap } = societeNotes;
    const result: SocieteRanking[] = [];

    societesMap.forEach(societe => {
      const note = notesMap.get(societe.id);
      
      // Build scores_criteres from the 4 global criteria
      const scores_criteres: { [key: string]: { score: number; count: number } } = {};
      
      if (note?.note_qualite_offre !== null && note?.note_qualite_offre !== undefined) {
        scores_criteres["Qualité de l'offre"] = { 
          score: (note.note_qualite_offre / 5) * 100, // Convert to percentage for consistency
          count: 1 
        };
      }
      if (note?.note_prix !== null && note?.note_prix !== undefined) {
        scores_criteres["Prix"] = { 
          score: (note.note_prix / 5) * 100,
          count: 1 
        };
      }
      if (note?.note_durabilite !== null && note?.note_durabilite !== undefined) {
        scores_criteres["Durabilité"] = { 
          score: (note.note_durabilite / 5) * 100,
          count: 1 
        };
      }
      if (note?.note_references !== null && note?.note_references !== undefined) {
        scores_criteres["Références"] = { 
          score: (note.note_references / 5) * 100,
          count: 1 
        };
      }

      // Calculate weighted score
      const poids_qualite_offre = note?.poids_qualite_offre ?? 25;
      const poids_prix = note?.poids_prix ?? 25;
      const poids_durabilite = note?.poids_durabilite ?? 25;
      const poids_references = note?.poids_references ?? 25;
      
      let totalWeight = 0;
      let weightedSum = 0;
      
      if (note?.note_qualite_offre !== null && note?.note_qualite_offre !== undefined) {
        weightedSum += note.note_qualite_offre * poids_qualite_offre;
        totalWeight += poids_qualite_offre;
      }
      if (note?.note_prix !== null && note?.note_prix !== undefined) {
        weightedSum += note.note_prix * poids_prix;
        totalWeight += poids_prix;
      }
      if (note?.note_durabilite !== null && note?.note_durabilite !== undefined) {
        weightedSum += note.note_durabilite * poids_durabilite;
        totalWeight += poids_durabilite;
      }
      if (note?.note_references !== null && note?.note_references !== undefined) {
        weightedSum += note.note_references * poids_references;
        totalWeight += poids_references;
      }
      
      const score_pondere = totalWeight > 0 ? (weightedSum / totalWeight / 5) * 100 : 0; // Convert to percentage

      result.push({
        societe_id: societe.id,
        societe_nom: societe.nom,
        nb_candidatures: 1, // Not relevant for global view
        score_moyen: score_pondere,
        decision_counts: {
          shortlist: score_pondere >= 70 ? 1 : 0,
          consider: score_pondere >= 40 && score_pondere < 70 ? 1 : 0,
          reject: score_pondere < 40 ? 1 : 0
        },
        candidats: [],
        scores_criteres
      });
    });

    // Sort by score descending
    return result.sort((a, b) => b.score_moyen - a.score_moyen);
  }, [societeNotes]);

  // Auto-select companies based on filter mode
  useEffect(() => {
    if (rankings.length === 0) return;

    if (filterMode === 'top') {
      const topCompanies = rankings.slice(0, topCount).map(r => r.societe_id);
      setSelectedCompanies(topCompanies);
    } else if (filterMode === 'decision') {
      const filteredCompanies = rankings.filter(ranking => {
        return selectedDecisions.some(decision => {
          if (decision === 'shortlist') return ranking.decision_counts.shortlist > 0;
          if (decision === 'consider') return ranking.decision_counts.consider > 0;
          if (decision === 'reject') return ranking.decision_counts.reject > 0;
          return false;
        });
      }).map(r => r.societe_id);
      setSelectedCompanies(filteredCompanies);
    }
    // For manual mode, keep existing selection
  }, [rankings, filterMode, topCount, selectedDecisions]);

  // Auto-select all criteria by default
  useEffect(() => {
    if (rankings.length > 0) {
      // Get all unique criteria from all rankings
      const allCriteria = Array.from(
        new Set(rankings.flatMap(r => Object.keys(r.scores_criteres)))
      );
      
      const allCriteriaKeys = [
        'overall_score',
        ...allCriteria.map(c => `criteria_${c}`),
      ];
      
      setSelectedCriteria(allCriteriaKeys);
    }
  }, [rankings]);

  const handleExportPdf = () => {
    if (!appelOffre) {
      toast({
        title: "Erreur",
        description: "Impossible d'exporter sans informations sur l'appel d'offres",
        variant: "destructive"
      });
      return;
    }

    if (selectedCompanies.length === 0) {
      toast({
        title: "Erreur",
        description: "Sélectionnez au moins une société pour l'export",
        variant: "destructive"
      });
      return;
    }

    exportGlobalComparisonToPdf(appelOffre, globalSocietesData, selectedCompanies, selectedCandidatesData);
    toast({
      title: "PDF généré",
      description: `Le rapport comparatif avec ${selectedCompanies.length} sociétés a été téléchargé`
    });
  };

  const renderVisualization = () => {
    switch (visualizationType) {
      case 'radar':
        return (
          <RadarComparison
            rankings={rankings}
            selectedCompanies={selectedCompanies}
            selectedCriteria={selectedCriteria}
          />
        );
      case 'heatmap':
        return (
          <HeatmapComparison
            rankings={rankings}
            selectedCompanies={selectedCompanies}
            selectedCriteria={selectedCriteria}
          />
        );
      case 'bar':
        return (
          <GroupedBarComparison
            rankings={rankings}
            selectedCompanies={selectedCompanies}
            selectedCriteria={selectedCriteria}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (rankings.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Aucune société avec des notes pour cet appel d'offres.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Vue comparative des critères globaux (Qualité, Prix, Durabilité, Références)
        </div>
        {appelOffre && (
          <Button onClick={handleExportPdf} variant="outline" className="gap-2 bg-blue-100 hover:bg-blue-200 border-blue-300">
            <FileDown className="h-4 w-4" />
            Comparaison globale PDF
          </Button>
        )}
      </div>
      
      <ComparisonFilters
        rankings={rankings}
        selectedCompanies={selectedCompanies}
        selectedCriteria={selectedCriteria}
        visualizationType={visualizationType}
        filterMode={filterMode}
        topCount={topCount}
        selectedDecisions={selectedDecisions}
        onCompaniesChange={setSelectedCompanies}
        onCriteriaChange={setSelectedCriteria}
        onVisualizationTypeChange={setVisualizationType}
        onFilterModeChange={setFilterMode}
        onTopCountChange={setTopCount}
        onDecisionsChange={setSelectedDecisions}
      />
      
      {renderVisualization()}
    </div>
  );
};
